import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function FindReplacePanel({ textareaRef, markdown, setMarkdown }) {
  const { t } = useTranslation();
  const [find, setFind] = useState("");
  const [replace, setReplace] = useState("");
  const [caseSensitive, setCaseSensitive] = useState(false);

  const indexOfFrom = useCallback(
    (haystack, needle, from) => {
      if (!needle) return -1;
      if (caseSensitive) return haystack.indexOf(needle, from);
      const h = haystack.toLowerCase();
      const n = needle.toLowerCase();
      return h.indexOf(n, from);
    },
    [caseSensitive],
  );

  const findNext = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta || !find) return;
    const start = ta.selectionEnd;
    let idx = indexOfFrom(markdown, find, start);
    if (idx === -1 && start > 0) {
      idx = indexOfFrom(markdown, find, 0);
    }
    if (idx === -1) return;
    ta.focus();
    ta.setSelectionRange(idx, idx + find.length);
  }, [find, indexOfFrom, markdown, textareaRef]);

  const replaceOne = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta || !find) return;
    const { selectionStart, selectionEnd } = ta;
    const slice = markdown.slice(selectionStart, selectionEnd);
    const match = caseSensitive
      ? slice === find
      : slice.toLowerCase() === find.toLowerCase();
    if (!match) {
      findNext();
      return;
    }
    const next =
      markdown.slice(0, selectionStart) +
      replace +
      markdown.slice(selectionEnd);
    setMarkdown(next);
    const newPos = selectionStart + replace.length;
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.setSelectionRange(newPos, newPos);
        textareaRef.current.focus();
      }
    });
  }, [caseSensitive, find, findNext, markdown, replace, setMarkdown, textareaRef]);

  const replaceAll = useCallback(() => {
    if (!find) return;
    if (!window.confirm(t("mdwriter.replaceAllConfirm", { term: find }))) return;
    const re = new RegExp(escapeRegExp(find), caseSensitive ? "g" : "gi");
    setMarkdown(markdown.replace(re, replace));
  }, [caseSensitive, find, markdown, replace, setMarkdown, t]);

  return (
    <details className="editor-tools-details">
      <summary className="editor-tools-details__summary">
        {t("mdwriter.findReplace")}
        <span className="editor-tools-details__hint">{t("mdwriter.findReplaceHint")}</span>
      </summary>
      <div className="editor-find-replace" role="group" aria-label={t("mdwriter.findReplaceGroupAria")}>
        <label className="editor-find-field">
          <span className="editor-meta-label">{t("mdwriter.findLabel")}</span>
          <input
            className="editor-meta-input"
            value={find}
            onChange={(e) => setFind(e.target.value)}
            placeholder={t("mdwriter.findPh")}
            autoComplete="off"
          />
        </label>
        <label className="editor-find-field">
          <span className="editor-meta-label">{t("mdwriter.replaceLabel")}</span>
          <input
            className="editor-meta-input"
            value={replace}
            onChange={(e) => setReplace(e.target.value)}
            placeholder={t("mdwriter.replacePh")}
            autoComplete="off"
          />
        </label>
        <label className="editor-toggle editor-toggle--inline">
          <span className="editor-toggle-check-wrap">
            <input
              type="checkbox"
              className="editor-toggle-input"
              checked={caseSensitive}
              onChange={(e) => setCaseSensitive(e.target.checked)}
            />
            <span className="editor-toggle-indicator" aria-hidden="true" />
          </span>
          <span className="editor-toggle-label">{t("mdwriter.caseSensitive")}</span>
        </label>
        <div className="editor-find-actions">
          <button type="button" className="btn-tool" onClick={findNext}>
            {t("mdwriter.findNext")}
          </button>
          <button type="button" className="btn-tool" onClick={replaceOne}>
            {t("mdwriter.replace")}
          </button>
          <button type="button" className="btn-tool btn-tool--warn" onClick={replaceAll}>
            {t("mdwriter.replaceAll")}
          </button>
        </div>
      </div>
    </details>
  );
}
